import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { AuthFormIntro } from '@/components/auth/shell/auth-form-intro';
import { AuthSplitShell } from '@/components/auth/shell/auth-split-shell';
import { AuthStepper } from '@/components/auth/shell/auth-stepper';
import { CopilotStageCard } from '@/components/auth/onboarding-copilot/stage-card/copilot-stage-card';
import { connectChecklistRows } from '@/components/auth/connect/connect-checklist';
import {
  connectCopilotView,
  connectStepReady,
} from '@/components/auth/connect/connect-copilot';
import { ConnectHandoff } from '@/components/auth/connect/connect-handoff';
import { StepConnect } from '@/components/auth/connect/step-connect';
import { useConnectHandoff } from '@/components/auth/connect/use-connect-handoff';
import { ROUTES } from '@/constants/routes';
import { meQueryOptions } from '@/hooks/data/use-auth';
import { useOrganizationActivityStream } from '@/hooks/data/use-organization-activity-stream';
import {
  useCompleteOnboarding,
  useOrganizationSetupStatus,
} from '@/hooks/data/use-organizations';
import { TypedApiError } from '@/lib/typed-api-error';
import { loadNamespaces } from '@/lib/i18n/i18n';

export const Route = createFileRoute('/_authenticated/connect-platforms')({
  loader: () => loadNamespaces('auth', 'onboarding', 'settings'),
  staticData: { title: 'route.connect_platforms.title', hideAppShell: true },
  component: ConnectPlatformsRoute,
});

function ConnectPlatformsRoute(): React.ReactElement {
  const { t } = useTranslation('onboarding');
  const navigate = useNavigate();
  const { data: user } = useQuery(meQueryOptions);

  const organizationId = user?.organization_id ?? null;
  const complete = useCompleteOnboarding();

  // Single onboarding subscription for the route — shared by the platform
  // rows (StepConnect) and the Copilot stage card.
  const { pipelineByPlatform, stepsByPlatform, onboardingComplete, jobs } =
    useOrganizationActivityStream();
  const setupStatus = useOrganizationSetupStatus();
  const stream = {
    pipelineByPlatform,
    jobs,
    setupStatus: setupStatus.data,
  };
  const handoff = useConnectHandoff({
    ready: connectStepReady({
      onboardingComplete,
      setupStatus: setupStatus.data,
    }),
    settled: !setupStatus.isPending,
  });

  const steps = [
    { key: 'organization', label: t('stepper.organization_label') },
    { key: 'connect', label: t('stepper.connect_label') },
  ] as const;

  const goBack = (): void => {
    void navigate({ to: ROUTES.ORGANIZATION_SETUP });
  };

  // Flipping `onboarding_completed` is what locks the user out of the
  // onboarding pages — this is now the last step of the wizard.
  const finishOnboarding = async (): Promise<void> => {
    if (organizationId == null) {
      toast.error(t('connect_platforms.error_no_organization'));
      return;
    }
    if (user?.organization?.onboarding_completed) {
      void navigate({ to: ROUTES.DASHBOARD });
      return;
    }
    try {
      await complete.mutateAsync(organizationId);
      void navigate({ to: ROUTES.DASHBOARD });
    } catch (error) {
      toast.error(
        error instanceof TypedApiError
          ? error.detail
          : t('connect_platforms.error_generic'),
      );
    }
  };

  return (
    <AuthSplitShell
      topBar={
        <AuthStepper steps={steps.map((step) => step.label)} current={1} />
      }
      stage={
        <CopilotStageCard
          {...connectCopilotView({ ...stream, onboardingComplete }, t)}
          rows={connectChecklistRows({ ...stream, stepsByPlatform }, t)}
        />
      }
    >
      {handoff.shown ? (
        <ConnectHandoff
          onBack={goBack}
          onBookCall={handoff.bookCall}
          onContinueOnMobile={handoff.continueOnMobile}
        />
      ) : (
        <>
          <AuthFormIntro title={t('connect_platforms.form_title')} />
          <StepConnect
            organizationId={organizationId}
            onBack={goBack}
            onContinue={() => void finishOnboarding()}
            pipelineByPlatform={pipelineByPlatform}
            onboardingComplete={onboardingComplete}
            isPending={complete.isPending}
          />
        </>
      )}
    </AuthSplitShell>
  );
}
